import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import Timer from './Timer';
import { ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/react';
import Leaderboard from './Leaderboard';
import { useNavigate } from 'react-router-dom';
import AdSenseAd from './AdSenseAd';
import ShareButton from './ShareButton';
import { getOrCreatePlayerToken, clearPlayerToken } from '../playerToken';

interface MinimalSocket {
  on(event: string, callback: (...args: any[]) => void): void;
  emit(event: string, ...args: any[]): void;
  disconnect(): void;
  id: string;
  connected: boolean;
}

interface GameState {
  status: string;
  question?: string;
  options?: string[];
  correctAnswer?: string;
  timerEnd?: number;
  score?: number;
  leaderboard?: { name: string; score: number; pointsEarned?: number }[];
  questionNumber?: number;
}

interface NewQuestionData {
  question: string;
  options: string[];
  timerEnd: number;
  questionNumber: number;
}

interface WaitingData {
  question: string;
  timerEnd: number;
  questionNumber: number;
}

interface RevealAnswerData {
  question: string;
  options: string[];
  correctAnswer: string;
  questionNumber: number;
  leaderboard: { name: string; score: number; pointsEarned?: number }[];
}

interface PersonalStats {
  pointsEarned: number;
  basePoints: number;
  speedBonus: number;
  streakBonus: number;
  currentStreak: number;
  correct: boolean;
  rank: number;
  previousRank: number;
  totalPlayers: number;
}

interface WagerData {
  score: number;
  timerEnd: number;
  leaderboard: { name: string; score: number }[];
}

interface FinalQuestionData {
  question: string;
  options: string[];
  timerEnd: number;
  questionNumber: number;
}

interface GameOverData {
  leaderboard: { name: string; score: number }[];
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="text-red-400 text-center">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
    </div>
  );
}

const ALL_CATEGORIES: { id: string; label: string }[] = [
  { id: 'general_knowledge', label: 'General' },
  { id: 'science', label: 'Science' },
  { id: 'history', label: 'History' },
  { id: 'film_and_tv', label: 'Film & TV' },
  { id: 'music', label: 'Music' },
  { id: 'geography', label: 'Geography' },
  { id: 'sport_and_leisure', label: 'Sports' },
  { id: 'arts_and_literature', label: 'Arts & Lit' },
  { id: 'food_and_drink', label: 'Food & Drink' },
  { id: 'society_and_culture', label: 'Society' },
];
const DIFFICULTIES = ['mixed', 'easy', 'medium', 'hard'] as const;
type Difficulty = typeof DIFFICULTIES[number];

function GamePlay() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const location = useLocation();
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [playerList, setPlayerList] = useState<string[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [submittedAnswer, setSubmittedAnswer] = useState<string | null>(null);
  const [wager, setWager] = useState<number>(0);
  const [wagerSubmitted, setWagerSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [score, setScore] = useState<number>(0);
  const [personalStats, setPersonalStats] = useState<PersonalStats | null>(null);
  const navigate = useNavigate();
  const socketRef = useRef<MinimalSocket | null>(null);

  const storedPlayerName = sessionStorage.getItem('playerName');
  const navPlayerName = location.state ? (location.state as any).playerName : null;
  const playerName = navPlayerName || storedPlayerName || 'Unknown';
  const navIsHost = location.state ? !!(location.state as any).isHost : false;
  const storedIsHost = sessionStorage.getItem('isHost') === 'true';
  const isHost = navIsHost || storedIsHost;
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => ALL_CATEGORIES.map((c) => c.id));
  const [difficulty, setDifficulty] = useState<Difficulty>('mixed');

  useEffect(() => {
    if (!roomCode) {
      setError('Missing room code');
      setIsLoading(false);
      return;
    }
    
    if (!navPlayerName && !storedPlayerName) {
      setError('Player name not set; using default');
    }

    const serverUrl = import.meta.env.VITE_SERVER_URL;
    const newSocket = io(serverUrl, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    socketRef.current = newSocket;
    
    newSocket.on('connect', () => {
      const hostToken = isHost ? sessionStorage.getItem('hostToken') || undefined : undefined;
      const playerToken = roomCode ? getOrCreatePlayerToken(roomCode) : undefined;
      newSocket.emit('joinRoom', { roomCode, playerName, hostToken, playerToken });
    });
    newSocket.on('playerList', (playerNames: string[]) => {
      setPlayerList(playerNames);
      setIsLoading(false);
    });

    newSocket.on('connect_error', (err: Error) => {
      Sentry.captureException(err);
      setError('Failed to connect to server. Retrying...');
      setIsLoading(false);
    });

    newSocket.on('error', (msg: string) => {
      Sentry.captureMessage(msg);
      setError(msg);
    });

    newSocket.on('gameStarting', () => {
      setGameState({ status: 'starting', timerEnd: Date.now() + 5000 });
      setScore(0);
      setPersonalStats(null);
      setSubmittedAnswer(null);
      setSelectedAnswer(null);
      setShowNewCategoriesPicker(false);
    });

    newSocket.on('newQuestion', (data: NewQuestionData) => {
      setGameState({ status: 'question', ...data });
      setPersonalStats(null);
      setSubmittedAnswer(null);
      setSelectedAnswer(null);
    });

    newSocket.on('personalStats', (data: PersonalStats) => setPersonalStats(data));

    newSocket.on('waiting', (data: WaitingData) =>
      setGameState({ status: 'waiting', ...data })
    );

    newSocket.on('answerSubmitted', () =>
      setGameState((prev) => (prev ? { ...prev, status: 'waiting' } : null))
    );

    newSocket.on('revealAnswer', (data: RevealAnswerData) =>
      setGameState({ status: 'reveal', ...data })
    );

    newSocket.on('updateScore', (data: { score: number }) => setScore(data.score));

    newSocket.on('finalQuestionWager', (data: WagerData) => {
      setScore(data.score);
      setGameState({ status: 'wager', score: data.score || 0, timerEnd: data.timerEnd, leaderboard: data.leaderboard });
      setWager(0);
      setWagerSubmitted(false);
    });

    newSocket.on('wagerSubmitted', () => {
      setWagerSubmitted(true);
    });

    newSocket.on('finalQuestion', (data: FinalQuestionData) => {
      setGameState({ status: 'finalQuestion', ...data });
      setSelectedAnswer(null);
      setSubmittedAnswer(null);
      setPersonalStats(null);
    });

    newSocket.on('finalWaiting', (data: WaitingData) =>
      setGameState({ status: 'finalWaiting', ...data })
    );

    newSocket.on('finalAnswerSubmitted', () => {
      setGameState((prev) => (prev ? { ...prev, status: 'finalWaiting' } : null));
    });

    newSocket.on('gameOver', (data: GameOverData) => {
      setGameState({ status: 'over', leaderboard: data.leaderboard });
    });

    return () => {
      newSocket.disconnect();
    };
  }, [roomCode, navigate]);

  const submitAnswer = () => {
    try {
      if (selectedAnswer && socketRef.current) {
        socketRef.current.emit('submitAnswer', selectedAnswer);
        setSubmittedAnswer(selectedAnswer);
        setSelectedAnswer(null);
      }
    } catch (err) {
      Sentry.captureException(err);
      setError('Failed to submit answer'); 
    }
  };

  const submitWager = () => {
    try {
      const wagerValue = Number(wager);
      if (wagerValue >= 0 && wagerValue <= score && !wagerSubmitted && socketRef.current) {
        socketRef.current.emit('submitWager', wagerValue);
        setWagerSubmitted(true);
      } else {
        setError('Invalid wager amount');
      }
    } catch (err) {
      Sentry.captureException(err);
      setError('Failed to submit wager');
    }
  };

  const toggleCategory = (id: string) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const toggleAllCategories = () => {
    setSelectedCategories((prev) =>
      prev.length > 0 ? [] : ALL_CATEGORIES.map((c) => c.id)
    );
  };

  const startGame = () => {
    if (selectedCategories.length === 0) {
      alert('Pick at least one category');
      return;
    }
    if (window.confirm('Is everyone ready to start the game?')) {
      try {
        socketRef.current?.emit('startGame', { categories: selectedCategories, difficulty });
      } catch (err) {
        Sentry.captureException(err);
        alert('Failed to start game');
      }
    }
  };

  const [showNewCategoriesPicker, setShowNewCategoriesPicker] = useState<boolean>(false);

  const startNewGameSameCategories = () => {
    if (window.confirm('Start a new game with the same categories and players?')) {
      try {
        socketRef.current?.emit('startNewGame');
      } catch (err) {
        Sentry.captureException(err);
        alert('Failed to start new game');
      }
    }
  };

  const startNewGameWithSettings = () => {
    if (selectedCategories.length === 0) {
      alert('Pick at least one category');
      return;
    }
    if (window.confirm('Start a new game with the chosen categories?')) {
      try {
        socketRef.current?.emit('startNewGame', { categories: selectedCategories, difficulty });
      } catch (err) {
        Sentry.captureException(err);
        alert('Failed to start new game');
      }
    }
  };

  const submitFinalAnswer = () => {
    try {
      if (selectedAnswer && socketRef.current) {
        socketRef.current.emit('submitFinalAnswer', selectedAnswer);
        setSubmittedAnswer(selectedAnswer);
        setSelectedAnswer(null);
      }
    } catch (err) {
      Sentry.captureException(err);
      setError('Failed to submit final answer');
    }
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className={`flex flex-col items-center w-full max-w-2xl mx-auto ${gameState && gameState.status !== 'over' ? 'min-h-[640px]' : ''}`}>
        {!gameState && !isLoading && (
        <div className="text-center w-full">
          <div className="mb-6">
            {isHost && (
              <p className="text-lg mb-2">
                Room Code: <span className="text-yellow-300 font-semibold">{roomCode}</span>
              </p>
            )}
            <p className="text-sm text-gray-400">
              Answer fast for more points, then wager on the final question.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            {isHost && (
              <button
                onClick={startGame}
                disabled={selectedCategories.length === 0}
                className={`text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg ${
                  selectedCategories.length === 0
                    ? 'bg-blue-600 opacity-50 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Start Game
              </button>
            )}
            {roomCode && <ShareButton roomCode={roomCode} />}
          </div>
          {isHost && (
            <div className="w-full max-w-lg mx-auto mb-6">
              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={toggleAllCategories}
                  className="text-sm text-gray-300 hover:text-white underline"
                >
                  {selectedCategories.length > 0 ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {ALL_CATEGORIES.map((cat) => {
                  const selected = selectedCategories.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition border ${
                        selected
                          ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'
                          : 'bg-gray-700 bg-opacity-50 hover:bg-opacity-70 text-gray-300 border-gray-600'
                      }`}
                    >
                      {selected ? '✓ ' : ''}{cat.label}
                    </button>
                  );
                })}
              </div>
              <h3 className="text-lg font-semibold mb-2 text-center">Difficulty</h3>
              <div className="flex justify-center gap-2 flex-wrap">
                {DIFFICULTIES.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition ${
                      difficulty === d
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-700 bg-opacity-50 hover:bg-opacity-70 text-gray-300'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!isHost && (
            <div className="ad-container mt-6 mb-6">
              <AdSenseAd />
            </div>
          )}
          <h2 className="text-2xl font-semibold mb-4">Players ({playerList.length})</h2>
          <ul className="mb-6 text-lg space-y-2">
            {playerList.map((name) => (
              <li key={name} className="bg-gray-800 bg-opacity-50 p-2 rounded-lg">
                {name}{name === playerName ? ' (You)' : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
      {gameState?.status === 'starting' && (
        <div className="flex-1 flex items-center justify-center text-xl font-medium">
          Game starting in <Timer endTime={gameState.timerEnd!} />
        </div>
      )}
      {gameState?.status === 'question' && (
        <>
          <p className="text-lg font-semibold mb-2 text-center">Question: {gameState.questionNumber} / 7</p>
          <p className="text-xl md:text-2xl mb-6 text-center">{gameState.question}</p>
          <div className="mb-4 text-center">
            <Timer endTime={gameState.timerEnd!} />
          </div>
          <div className="w-full space-y-4">
            {gameState.options!.map((opt) => (
              <button
                key={opt}
                onClick={() => setSelectedAnswer(opt)}
                className={`w-full p-2 sm:p-4 rounded-lg text-base sm:text-lg font-medium transition duration-200 ${
                  selectedAnswer === opt 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800 bg-opacity-50 hover:bg-opacity-75 text-white'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
          <div className="mt-6 h-12 flex justify-center">
            {selectedAnswer && (
              <button
                onClick={submitAnswer}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg"
              >
                Submit
              </button>
            )}
          </div>
        </>
      )}
      {gameState?.status === 'waiting' && (
        <div className="w-full">
          <p className="text-lg font-semibold mb-2 text-center">Question: {gameState.questionNumber} / 7</p>
          <p className="text-xl md:text-2xl mb-6 text-center">{gameState.question}</p>
          <div className="mb-4 text-center">
            <Timer endTime={gameState.timerEnd!} />
          </div>
          <p className="text-lg font-medium text-center text-gray-300">Waiting for everyone to answer...</p>
        </div>
      )}
      {gameState?.status === 'reveal' && (
        <div className="text-center">
          <p className="text-lg font-semibold mb-2">
            {gameState.questionNumber === 8 ? 'Final Question' : `Question: ${gameState.questionNumber} / 7`}
          </p>
          <p className="text-xl md:text-2xl mb-4">{gameState.question}</p>
          <div className="w-full space-y-3 mt-4">
            {gameState.options!.map((opt) => (
              <p
                key={opt}
                className={`w-full p-2 sm:p-4 rounded-lg text-base sm:text-lg font-medium ${
                  opt === gameState.correctAnswer
                    ? 'bg-green-500 bg-opacity-80 text-white'
                    : opt === submittedAnswer
                    ? 'bg-red-500 bg-opacity-80 text-white'
                    : 'bg-gray-800 bg-opacity-50 text-white'
                }`}
              >
                {opt}
              </p>
            ))}
          </div>
          <div className="mt-4 flex flex-col items-center gap-1">
            {gameState.questionNumber === 8 ? (
              submittedAnswer === gameState.correctAnswer ? (
                <p className="text-2xl font-bold text-green-400">Correct! +{wager} wager</p>
              ) : submittedAnswer ? (
                <p className="text-2xl font-bold text-red-400">Incorrect: -{wager} wager</p>
              ) : (
                <p className="text-xl font-semibold text-red-400">No answer</p>
              )
            ) : personalStats?.correct ? (
              <>
                <p className="text-2xl font-bold text-green-400">
                  +{personalStats.pointsEarned} points ({personalStats.basePoints} base + {personalStats.speedBonus} speed)
                </p>
                {personalStats.streakBonus > 0 && (
                  <p className="text-2xl font-bold text-orange-400">
                    +{personalStats.streakBonus} streak ({personalStats.currentStreak} in a row!) 🔥
                  </p>
                )}
                {personalStats.streakBonus === 0 && personalStats.currentStreak >= 2 && (
                  <p className="text-sm text-orange-300 mt-1">Streak: {personalStats.currentStreak}</p>
                )}
              </>
            ) : submittedAnswer ? (
              <p className="text-xl font-semibold text-red-400">Incorrect</p>
            ) : (
              <p className="text-xl font-semibold text-red-400">No answer</p>
            )}
          </div>
          {gameState.leaderboard && gameState.leaderboard.length > 0 && (
            <div className="mt-5 w-full max-w-sm mx-auto">
              <div className="space-y-1">
                {gameState.leaderboard.slice(0, 3).map((p, i) => {
                  const isMe = p.name === playerName;
                  const bg = isMe ? 'bg-blue-600 bg-opacity-80' : 'bg-gray-800 bg-opacity-60';
                  const earned = p.pointsEarned ?? 0;
                  return (
                    <div
                      key={`${p.name}-${i}`}
                      className={`flex justify-between px-3 py-2 rounded-lg text-base font-medium ${bg}`}
                    >
                      <span>{i + 1}. {p.name}{isMe ? ' (You)' : ''}</span>
                      <span>
                        {p.score}{' '}
                        <span className={earned > 0 ? 'text-green-400' : 'text-red-400'}>
                          (+{earned})
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {gameState?.status === 'wager' && (
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Final Wager</h2>
          <p className="text-lg mb-4">
            Wager up to your score — a right answer adds it, a wrong one loses it.
          </p>
          <Timer endTime={gameState.timerEnd!} />
          {wagerSubmitted ? (
            <p className="text-lg text-green-400 mt-4 mb-6">Wager Submitted: {wager} points</p>
          ) : (
            <div className="w-full mt-4 mb-6">
              <label htmlFor="wager-slider" className="text-lg font-medium">Your Wager: {wager} points</label>
              <input
                id="wager-slider"
                type="range"
                min="0"
                max={score}
                value={wager}
                onChange={(e) => setWager(Number(e.target.value))}
                className="w-full mt-2 accent-blue-600"
              />
              <div className="flex justify-between mt-2">
                <button
                  onClick={() => setWager(0)}
                  className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-3 py-1 rounded-md text-sm"
                >
                  0
                </button>
                <button
                  onClick={() => setWager(Math.floor(score / 2))}
                  className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-3 py-1 rounded-md text-sm"
                >
                  50%
                </button>
                <button
                  onClick={() => setWager(score)}
                  className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-3 py-1 rounded-md text-sm"
                >
                  All In
                </button>
              </div>
              <button
                onClick={submitWager}
                className="mt-4 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg"
              >
                Submit Wager
              </button>
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold mb-2">Current Standings</h3>
            {gameState.leaderboard && (
              <Leaderboard
                players={gameState.leaderboard}
                currentPlayer={playerName}
                highlightTop={false}
                showTitle={false}
              />
            )}
          </div>
        </div>
      )}
      {gameState?.status === 'finalQuestion' && (
        <>
          <p className="text-lg font-semibold mb-2">Final Question</p>
          <p className="text-xl md:text-2xl mb-6 text-center">{gameState.question}</p>
          <Timer endTime={gameState.timerEnd!} />
          <div className="w-full space-y-4 mt-4">
            {gameState.options!.map((opt) => (
              <button
                key={opt}
                onClick={() => setSelectedAnswer(opt)}
                className={`w-full p-2 sm:p-4 rounded-lg text-base sm:text-lg font-medium transition duration-200 ${
                  selectedAnswer === opt 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800 bg-opacity-50 hover:bg-opacity-75 text-white'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
          <div className="mt-6 h-12 flex justify-center">
            {selectedAnswer && (
              <button
                onClick={submitFinalAnswer}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg"
              >
                Submit
              </button>
            )}
          </div>
        </>
      )}
      {gameState?.status === 'finalWaiting' && (
        <div>
        <p className="text-xl font-medium mb-2 text-center">Final Question</p>
        <p className="text-xl md:text-2xl mb-6 text-center">{gameState.question}</p>
        <div className="mb-6 text-center">
          <Timer endTime={gameState.timerEnd!} />
        </div>
        <p className="text-xl font-medium text-center">Waiting for everyone to answer...</p>
      </div>
      )}
      {isLoading && <div className="text-xl">Connecting to server...</div>}
      {error && <div className="text-red-400 mb-4">{error}</div>}
      {gameState?.status === 'over' && gameState.leaderboard && (
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-6">Game Over!</h2>
          <div className="space-y-2">
            {gameState.leaderboard.map((p, index) => (
              <p
                key={`${p.name}-${index}`}
                className={`text-lg font-medium ${
                  index === 0 ? 'text-yellow-400 text-2xl' : 'text-white'
                }`}
              >
                {index + 1}. {p.name}: {p.score}
              </p>
            ))}
          </div>
          {isHost && showNewCategoriesPicker && (
            <div className="w-full max-w-lg mx-auto mt-6 mb-2 text-left">
              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={toggleAllCategories}
                  className="text-sm text-gray-300 hover:text-white underline"
                >
                  {selectedCategories.length > 0 ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {ALL_CATEGORIES.map((cat) => {
                  const selected = selectedCategories.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition border ${
                        selected
                          ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'
                          : 'bg-gray-700 bg-opacity-50 hover:bg-opacity-70 text-gray-300 border-gray-600'
                      }`}
                    >
                      {selected ? '✓ ' : ''}{cat.label}
                    </button>
                  );
                })}
              </div>
              <h3 className="text-lg font-semibold mb-2 text-center">Difficulty</h3>
              <div className="flex justify-center gap-2 flex-wrap">
                {DIFFICULTIES.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition ${
                      difficulty === d
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-700 bg-opacity-50 hover:bg-opacity-70 text-gray-300'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            {isHost && !showNewCategoriesPicker && (
              <>
                <button
                  onClick={startNewGameSameCategories}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg"
                >
                  Same Categories
                </button>
                <button
                  onClick={() => setShowNewCategoriesPicker(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg"
                >
                  New Categories
                </button>
              </>
            )}
            {isHost && showNewCategoriesPicker && (
              <>
                <button
                  onClick={startNewGameWithSettings}
                  disabled={selectedCategories.length === 0}
                  className={`text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg ${
                    selectedCategories.length === 0
                      ? 'bg-blue-600 opacity-50 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  Start Game
                </button>
                <button
                  onClick={() => setShowNewCategoriesPicker(false)}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg"
                >
                  Cancel
                </button>
              </>
            )}
            <button
              onClick={() => {
                if (roomCode) clearPlayerToken(roomCode);
                navigate('/');
              }}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg"
            >
              Back to Home
            </button>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
}

export default GamePlay;
